/**
 * EmailComposeDialog.jsx — Vague 113 L8 Wave 1b
 *
 * Modal dialog for composing a Gmail message. Supports:
 *  - New message (mode = 'new')
 *  - Reply / Reply-all (mode = 'reply' | 'reply_all')
 *  - Forward (mode = 'forward')
 *  - Editing an existing draft (props.draftId)
 *
 * Persists drafts to Gmail (auto-save every 15s of inactivity + on close).
 * Rich text via TipTap. Attachments via base64 (max 18 MB to stay below
 * Gmail's 25 MB encoded limit).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';import {
  X, Send, Save, Trash2, Paperclip, Bold, Italic, List, ListOrdered,
  Link as LinkIcon, Loader2, ChevronDown, ChevronUp, FileText, PenTool,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import EmailTemplatesPopover from './email/EmailTemplatesPopover';
import { formatAccountLabel, accountKey, accountHeaderValue } from './email/emailUtils';

const MAX_ATT_BYTES = 18 * 1024 * 1024; // 18 MB encoded ceiling for Gmail (25 MB raw)

// Wave 1u6 — TipTap's default schema doesn't allow arbitrary attributes on
// images and strips `<div>` wrappers entirely. We extend the Image extension
// so the signature image can carry a stable marker attribute (data-ofm-sig)
// AND its inline width style — both used by the swap + corner-drag-resize logic.
const SignatureImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      'data-ofm-sig': {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ofm-sig'),
        renderHTML: (attrs) => (attrs['data-ofm-sig'] ? { 'data-ofm-sig': attrs['data-ofm-sig'] } : {}),
      },
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute('style'),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute('width'),
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
    };
  },
});

function buildQuotedBlock(originalMsg) {
  if (!originalMsg) return '';
  const dateStr = originalMsg.date_ms
    ? new Date(originalMsg.date_ms).toLocaleString('fr-FR')
    : (originalMsg.date || '');
  const from = (originalMsg.from || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const body = originalMsg.body_html || (originalMsg.body_text || '').replace(/\n/g, '<br/>');
  return `<br/><br/><div style="border-left:2px solid #C7D2FE;padding-left:12px;color:#6B6C85;font-size:13px">
    <div style="margin-bottom:8px;font-style:italic">Le ${dateStr}, ${from} a écrit :</div>
    ${body}
  </div>`;
}

function buildForwardBlock(originalMsg) {
  if (!originalMsg) return '';
  const dateStr = originalMsg.date_ms
    ? new Date(originalMsg.date_ms).toLocaleString('fr-FR')
    : (originalMsg.date || '');
  const from = (originalMsg.from || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const to = (originalMsg.to || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const body = originalMsg.body_html || (originalMsg.body_text || '').replace(/\n/g, '<br/>');
  return `<br/><br/><div style="color:#1A1B2E;font-size:13px">
    <div style="border-top:1px solid #EFF0F6;padding-top:12px;color:#6B6C85;font-size:12px;margin-bottom:8px">
      ---------- Message transféré ----------<br/>
      <b>De :</b> ${from}<br/>
      <b>Date :</b> ${dateStr}<br/>
      <b>Objet :</b> ${(originalMsg.subject || '').replace(/</g, '&lt;')}<br/>
      <b>À :</b> ${to}
    </div>
    ${body}
  </div>`;
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:mime/type;base64,XXXX" — strip prefix
      const result = reader.result || '';
      const idx = result.indexOf(',');
      resolve(idx === -1 ? result : result.substring(idx + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function EmailComposeDialog({
  open,
  onClose,
  onSent,                  // called after successful send
  mode = 'new',            // 'new' | 'reply' | 'reply_all' | 'forward' | 'draft'
  originalThread = null,   // full thread (for reply/forward)
  draftId: initialDraftId = null,
  accounts = [],           // Wave 1u3 Phase B / J3 — list of mailboxes user can send from
  currentAccount = null,   // currently active account (from Email page sidebar)
}) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [signatureHtml, setSignatureHtml] = useState('');
  const [draftId, setDraftId] = useState(initialDraftId);
  const [attachments, setAttachments] = useState([]); // [{filename, mime_type, content_b64, size}]
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Wave 1u3 Phase B / J3 — selected "From" account for THIS compose only.
  // Initialized to the global selectedAccount and may be overridden by user.
  const [fromAccount, setFromAccount] = useState(currentAccount);
  // Wave 1u3 Phase C / F2 — mail templates popover state
  const [templatesOpen, setTemplatesOpen] = useState(false);
  // Vague 113 / Wave 1u4 — list of signatures the user can pick from
  // and the currently-applied one (id from /api/user/compose-signatures).
  const [signatures, setSignatures] = useState([]);
  const [selectedSignatureId, setSelectedSignatureId] = useState('');
  const [signatureOpen, setSignatureOpen] = useState(false);
  // Wave 1u5 — Signature scale (0.4-1.2). Default 0.6 → ~192px image / 8.1px font
  // — smaller than the previous 1.0 default so the signature feels lightweight.
  // The user can grow/shrink it via a continuous slider in the Signature menu.
  const [signatureScale, setSignatureScale] = useState(0.6);
  // Wave 1u4 — Template variables resolved from the thread's CRM match (if any).
  const [templateVars, setTemplateVars] = useState({});
  const fileInputRef = useRef(null);
  const lastEditTsRef = useRef(0);

  const editor = useEditor({
    extensions: [
      // StarterKit ships its own Link extension since v2.13 — disable it
      // to allow our explicit Link.configure() without a "duplicate extension" warning.
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, autolink: true }),
      // Allow inline images (e.g. signature logos from Paramètres → Boîte email)
      SignatureImage.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: 'tiptap-img' } }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-mailbox-editor outline-none min-h-[200px] p-3 text-[13.5px] text-[#1A1B2E]',
      },
    },
    onUpdate: () => { lastEditTsRef.current = Date.now(); },
  });

  // Wave 1u6 — Drag-to-resize the signature IMAGE by its bottom-right corner.
  // Listening on the editor's root DOM lets us reuse a single mousedown handler
  // (no per-render reattach loop). When the click lands within 18 px of the
  // bottom-right corner of a signature image, we capture mousemove/mouseup on
  // window and rewrite `max-width` proportionally — `height:auto` keeps the
  // aspect ratio intact. On mouseup we sync the new HTML back into the editor
  // state so the change persists in the draft / sent email.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return undefined;
    const dom = editor.view?.dom;
    if (!dom) return undefined;
    let dragging = null; // {img, startX, startWidth}
    const HIT = 18;

    const onDown = (e) => {
      const img = e.target?.closest?.('img');
      if (!img) return;
      // Wave 1u6 — the IMG itself carries the marker (the wrapping <div> was
      // stripped by TipTap's schema, so we attached `data-ofm-sig="1"` to the
      // <img> via the SignatureImage extension).
      if (img.getAttribute('data-ofm-sig') !== '1') return;
      const rect = img.getBoundingClientRect();
      const dx = rect.right - e.clientX;
      const dy = rect.bottom - e.clientY;
      // Only start the drag if the click landed in the bottom-right corner.
      if (dx < 0 || dx > HIT || dy < 0 || dy > HIT) return;
      e.preventDefault();
      e.stopPropagation();
      dragging = { img, startX: e.clientX, startWidth: rect.width };
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';
    };

    const onMove = (e) => {
      if (!dragging) return;
      const next = Math.max(40, Math.min(600, dragging.startWidth + (e.clientX - dragging.startX)));
      dragging.img.style.maxWidth = `${Math.round(next)}px`;
      dragging.img.style.width = `${Math.round(next)}px`;
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Commit the DOM mutation back into the editor's state. Without this,
      // TipTap would overwrite our inline-style change on the next render.
      try { editor.commands.setContent(dom.innerHTML, false); } catch { /* noop */ }
    };

    dom.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      dom.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [editor]);

  // ------- Init content based on mode --------------------------------------
  useEffect(() => {
    if (!open || !editor) return;
    let cancelled = false;
    (async () => {
      // 1) Load available signatures (Wave 1u4) — falls back to /user/signature.
      let sigList = [];
      let sigHtml = '';
      let initialSigId = '';
      try {
        const { data } = await api.get('/user/compose-signatures');
        sigList = Array.isArray(data?.signatures) ? data.signatures : [];
      } catch { /* optional */ }
      try {
        const { data } = await api.get('/user/signature');
        sigHtml = data?.signature_html || '';
      } catch { /* signature optional */ }
      if (cancelled) return;

      // Pick a default signature: prefer the cfg whose email matches the
      // currently-selected mailbox, otherwise the first cfg, otherwise the
      // user's universal signature.
      if (sigList.length > 0) {
        const selectedEmail = (currentAccount || '').split(':').slice(1).join(':');
        const matchByAccount = sigList.find((s) => s.email && (
          s.email.toLowerCase() === selectedEmail.toLowerCase() ||
          (s.id === 'user_perso' && (currentAccount || '').startsWith('user'))
        ));
        const chosen = matchByAccount || sigList[0];
        if (chosen) {
          initialSigId = chosen.id;
          sigHtml = chosen.html;
        }
      }
      setSignatures(sigList);
      setSelectedSignatureId(initialSigId);
      setSignatureHtml(sigHtml);

      // Wave 1u4 — fetch CRM-aware template variables for this thread (if any)
      const tid = originalThread?.thread_id;
      if (tid) {
        try {
          const { data } = await api.get(`/mailbox/threads/${tid}/template-variables`);
          if (!cancelled) setTemplateVars(data?.variables || {});
        } catch { /* optional */ }
      } else {
        // New email (no thread): still expose `today` + `user_*` defaults
        try {
          const { data } = await api.get('/mailbox/threads/none/template-variables');
          if (!cancelled) setTemplateVars(data?.variables || {});
        } catch { /* optional */ }
      }

      // 2) If editing a draft → load it
      if (initialDraftId) {
        try {
          const { data } = await api.get(`/mailbox/drafts/${initialDraftId}`);
          if (cancelled) return;
          setTo(data?.to || '');
          setCc(data?.cc || '');
          setBcc(data?.bcc || '');
          setSubject(data?.subject || '');
          setDraftId(data?.draft_id);
          if (editor && !editor.isDestroyed) {
            editor.commands.setContent(data?.body_html || data?.body_text || '');
          }
        } catch (err) {
          toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Brouillon introuvable' });
        }
        return;
      }

      // 3) Reply / Forward / New
      const lastMsg = originalThread?.messages?.[originalThread.messages.length - 1];
      let initialBody = '';
      // Wave 1u6 — use buildScaledSignature so the IMG gets the marker attribute
      // needed for swap/corner-resize to find the signature later.
      if (sigHtml) {
        initialBody += `<p></p><p></p>${buildScaledSignature(sigHtml, signatureScale)}`;
      }

      if (mode === 'reply' || mode === 'reply_all') {
        // Strip "Re:" if already present
        const sub = (lastMsg?.subject || '').replace(/^(re|ré)\s*:\s*/i, '');
        setSubject(`Re: ${sub}`);
        setTo(lastMsg?.from || '');
        if (mode === 'reply_all') {
          // Reply-all: keep all CC recipients (excluding ourselves)
          const ccList = (lastMsg?.cc || '').split(',').map((s) => s.trim()).filter(Boolean);
          // Keep any "To" of the original that wasn't us
          const toList = (lastMsg?.to || '').split(',').map((s) => s.trim()).filter(Boolean);
          if (toList.length || ccList.length) {
            setCc([...toList, ...ccList].join(', '));
            setShowCcBcc(true);
          }
        }
        initialBody += buildQuotedBlock(lastMsg);
      } else if (mode === 'forward') {
        const sub = (lastMsg?.subject || '').replace(/^(fwd|tr)\s*:\s*/i, '');
        setSubject(`Tr: ${sub}`);
        initialBody += buildForwardBlock(lastMsg);
        // Copy attachments metadata as a note (we can't auto-download/reattach without extra clicks)
      } else {
        setSubject('');
      }
      if (editor && !editor.isDestroyed) {
        editor.commands.setContent(initialBody);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editor, mode, initialDraftId]);

  // ------- Reset when closed -----------------------------------------------
  useEffect(() => {
    if (!open) {
      setTo(''); setCc(''); setBcc(''); setSubject('');
      setShowCcBcc(false);
      setAttachments([]); setDraftId(null); setDraftSavedAt(null);
      if (editor && !editor.isDestroyed) editor.commands.clearContent();
    } else {
      // Wave 1u3 / J3 — sync the per-compose account override with the global one
      // every time the dialog opens.
      setFromAccount(currentAccount);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ------- Auto-save draft every 15s of idle -------------------------------
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      const idle = Date.now() - lastEditTsRef.current;
      if (idle > 15000 && idle < 30000) {
        // Only save if user has touched something recently but stopped typing
        if (!savingDraft && (to || subject || (editor?.getText() || '').trim())) {
          saveDraft(false);
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, to, subject, draftId]);

  const replyContext = useMemo(() => {
    if (!originalThread || mode === 'new' || mode === 'forward') return {};
    const lastMsg = originalThread.messages[originalThread.messages.length - 1];
    return {
      thread_id: originalThread.thread_id,
      in_reply_to: lastMsg?.message_id ? `<${lastMsg.message_id}>` : undefined,
      references: lastMsg?.message_id ? `<${lastMsg.message_id}>` : undefined,
    };
  }, [originalThread, mode]);

  // ------- Attachments -----------------------------------------------------
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking same file
    if (!files.length) return;
    const total = attachments.reduce((s, a) => s + a.size, 0);
    let acc = total;
    const newOnes = [];
    for (const f of files) {
      if (acc + f.size > MAX_ATT_BYTES) {
        toast({ title: 'Pièces jointes trop volumineuses', description: 'Limite ~18 Mo au total.' });
        break;
      }
      try {
        const b64 = await fileToBase64(f);
        newOnes.push({ filename: f.name, mime_type: f.type || 'application/octet-stream', content_b64: b64, size: f.size });
        acc += f.size;
      } catch {
        toast({ title: 'Erreur', description: `Impossible de lire ${f.name}` });
      }
    }
    if (newOnes.length) setAttachments((prev) => [...prev, ...newOnes]);
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  // ------- Save draft ------------------------------------------------------
  const payload = () => ({
    to, cc: cc || null, bcc: bcc || null,
    subject,
    body_html: editor?.getHTML() || '',
    body_text: editor?.getText() || '',
    attachments: attachments.map(({ filename, mime_type, content_b64 }) => ({ filename, mime_type, content_b64 })),
    ...replyContext,
  });

  // Build the X-Mailbox-Account header value for the currently-selected
  // From option. axios's global interceptor reads `ifaap_mailbox_account`
  // from sessionStorage, but we override it here per-compose so a shared
  // mailbox choice doesn't leak into other tabs.
  const headerFor = (val) => {
    if (!val || !accounts || accounts.length === 0) return '';
    const acct = accounts.find((a, idx) => accountKey(a, idx) === val);
    return accountHeaderValue(acct) || '';
  };

  const saveDraft = async (showToast = true) => {
    if (savingDraft) return;
    setSavingDraft(true);
    try {
      // Wave 1u3 / J3 — explicitly send the chosen account in the header so
      // axios's global interceptor doesn't override it with a stale sessionStorage value.
      const h = headerFor(fromAccount);
      const cfg = h ? { headers: { 'X-Mailbox-Account': h } } : {};
      if (draftId) {
        await api.put(`/mailbox/drafts/${draftId}`, payload(), cfg);
      } else {
        const { data } = await api.post('/mailbox/drafts', payload(), cfg);
        if (data?.draft_id) setDraftId(data.draft_id);
      }
      setDraftSavedAt(new Date());
      if (showToast) toast({ title: 'Brouillon enregistré' });
    } catch (err) {
      if (showToast) toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSavingDraft(false); }
  };

  const discardDraft = async () => {
    if (!draftId) { onClose(); return; }
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    try {
      const h = headerFor(fromAccount);
      const cfg = h ? { headers: { 'X-Mailbox-Account': h } } : {};
      await api.delete(`/mailbox/drafts/${draftId}`, cfg);
      toast({ title: 'Brouillon supprimé' });
    } catch { /* ignore */ }
    onClose();
  };

  // ------- Send ------------------------------------------------------------
  const send = async () => {
    if (!to.trim()) { toast({ title: 'Destinataire manquant' }); return; }
    setSending(true);
    try {
      const h = headerFor(fromAccount);
      const cfg = h ? { headers: { 'X-Mailbox-Account': h } } : {};
      await api.post('/mailbox/send', { ...payload(), draft_id: draftId }, cfg);
      toast({ title: 'Message envoyé' });
      if (onSent) onSent();
      onClose();
    } catch (err) {
      toast({ title: 'Erreur d\'envoi', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSending(false); }
  };

  if (!open) return null;

  // Wave 1u4 — replace `{{var_name}}` placeholders using the resolved CRM
  // variables. Unknown variables are kept verbatim so the user notices typos.
  const interpolate = (text) => {
    if (!text) return text;
    return String(text).replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (
      templateVars[k] != null ? String(templateVars[k]) : m
    ));
  };

  // Wave 1u6 — Replace the signature block. Since TipTap strips the wrapping
  // `<div data-ofm-signature="1">`, we use a marker attribute on the SIGNATURE
  // IMAGE (`data-ofm-sig="1"`, preserved via the SignatureImage extension) as
  // the anchor. Everything from that image to the end of the document is
  // considered "the signature" — we delete it and replace with the new one.
  // This avoids the bug where the previous regex matched only up to the first
  // </div> and left half of the previous signature behind.
  const swapSignatureInEditor = (newSigBlockHtml) => {
    if (!editor || editor.isDestroyed) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<body>${editor.getHTML()}</body>`, 'text/html');
    const marker = doc.body.querySelector('img[data-ofm-sig="1"]');
    if (marker) {
      // Walk up to the IMG's top-level block parent, then remove that and
      // every following sibling — the existing signature lives there.
      let node = marker;
      while (node.parentElement && node.parentElement !== doc.body) {
        node = node.parentElement;
      }
      let cursor = node;
      while (cursor) {
        const next = cursor.nextSibling;
        cursor.remove();
        cursor = next;
      }
    }
    if (newSigBlockHtml) {
      doc.body.insertAdjacentHTML('beforeend', newSigBlockHtml);
    }
    editor.commands.setContent(doc.body.innerHTML);
  };

  // Wave 1u5 — Build a signature HTML block scaled to a given ratio. Scales:
  //   • font-size of the wrapper div (text scales linearly)
  //   • max-width:Npx on every <img> inside (images stay proportional via height:auto)
  //   • marks the FIRST <img> with data-ofm-sig="1" so swap/resize can find it
  const buildScaledSignature = (rawHtml, scale) => {
    if (!rawHtml) return '';
    const fontPx = (13.5 * scale).toFixed(1);
    // Rewrite max-width:Npx → max-width:(N*scale)px so the image shrinks/grows
    let scaled = String(rawHtml).replace(
      /max-width\s*:\s*(\d+)px/gi,
      (_, n) => `max-width:${Math.round(parseInt(n, 10) * scale)}px`,
    );
    // Tag the first <img> with our marker (only if no other already has it)
    if (!/data-ofm-sig=/.test(scaled)) {
      scaled = scaled.replace(/<img\b/i, '<img data-ofm-sig="1"');
    }
    return `<div style="font-size:${fontPx}px">${scaled}</div>`;
  };

  // Vague 113 / Wave 1u4 — Swap the previously-inserted signature with the
  // newly-chosen one. Uses the DOM-based `swapSignatureInEditor` so nested
  // <div>s inside the signature don't truncate the replacement.
  const applySignature = (sig) => {
    if (!editor || editor.isDestroyed) return;
    const html = sig?.html || '';
    const styled = html ? buildScaledSignature(html, signatureScale) : '';
    swapSignatureInEditor(styled);
    setSignatureHtml(html);
    setSelectedSignatureId(sig?.id || '');
    setSignatureOpen(false);
  };

  // Wave 1u4 — Adjust signature size in-place (rewraps the existing marker div).
  const applySignatureScale = (scale) => {
    setSignatureScale(scale);
    if (!editor || editor.isDestroyed || !signatureHtml) return;
    swapSignatureInEditor(buildScaledSignature(signatureHtml, scale));
  };

  // When the user changes the "From" mailbox we automatically swap the
  // signature to the one of the matching email_config (if any). Users can
  // still override afterwards via the Signature dropdown.
  const handleFromChange = (val) => {
    setFromAccount(val);
    if (!signatures.length) return;
    const acct = (accounts || []).find((a) => accountKey(a, 0).startsWith(a.provider + ':') && (
      (a.shared_config_id && val === `${a.provider}:${a.shared_config_id}`) ||
      (!a.shared_config_id && val === `${a.provider}:${a.email || ''}`)
    )) || (accounts || []).find((a, idx) => accountKey(a, idx) === val);
    const targetEmail = (acct?.email || '').toLowerCase();
    const match = signatures.find((s) => (s.email || '').toLowerCase() === targetEmail);
    if (match && match.id !== selectedSignatureId) applySignature(match);
  };

  const formatBytes = (b) => b < 1024 ? `${b} o` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} Ko` : `${(b / 1048576).toFixed(2)} Mo`;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-end p-4 pointer-events-none" data-testid="email-compose-dialog">
      <div className="absolute inset-0 bg-black/10 pointer-events-auto" onClick={() => saveDraft(false).then(onClose).catch(onClose)} />
      <div className="relative pointer-events-auto w-full max-w-2xl h-[640px] bg-white rounded-t-xl shadow-2xl flex flex-col border border-[#EFF0F6]">
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-2.5 border-b border-[#EFF0F6] bg-[#1A1B2E] text-white rounded-t-xl">
          <div className="text-[13px] font-semibold">
            {initialDraftId ? 'Modifier le brouillon' :
              mode === 'reply' ? 'Répondre' :
              mode === 'reply_all' ? 'Répondre à tous' :
              mode === 'forward' ? 'Transférer' : 'Nouveau message'}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => saveDraft(false).then(onClose).catch(onClose)}
              className="h-7 w-7 rounded hover:bg-white/10 flex items-center justify-center"
              title="Réduire (enregistre en brouillon)"
              data-testid="email-compose-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Recipients */}
        <div className="px-4 py-2 border-b border-[#EFF0F6] space-y-1.5">
          {/* Vague 113 / Wave 1u4 — From-account selector (always visible).
              The user can pick which mailbox (personal vs shared) to send from. */}
          {accounts && accounts.length > 0 && (
            <div className="flex items-center gap-2" data-testid="email-compose-from-row">
              <label className="text-[11px] font-semibold text-[#6B6C85] w-12">De</label>
              {accounts.length > 1 ? (
                <select
                  data-testid="email-compose-from"
                  value={fromAccount || ''}
                  onChange={(e) => handleFromChange(e.target.value)}
                  className="flex-1 h-8 px-2 text-[12.5px] bg-transparent focus:outline-none border-b border-transparent focus:border-[#C7D2FE]"
                >
                  {accounts.filter((a) => a.can_send !== false).map((a, idx) => {
                    const val = accountKey(a, idx);
                    const label = formatAccountLabel(a);
                    const suffix = a.shared_config_id ? '  ·  partagée' : (a.provider === 'shared_imap' ? '  ·  partagée' : '');
                    return <option key={val} value={val}>{label}{suffix}</option>;
                  })}
                </select>
              ) : (
                <div className="flex-1 text-[12.5px] text-[#1A1B2E] truncate" data-testid="email-compose-from-static">
                  {formatAccountLabel(accounts[0])}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold text-[#6B6C85] w-12">À</label>
            <input
              data-testid="email-compose-to"
              value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinataire@exemple.com"
              className="flex-1 h-8 px-2 text-[12.5px] bg-transparent focus:outline-none border-b border-transparent focus:border-[#C7D2FE]"
            />
            <button
              onClick={() => setShowCcBcc(!showCcBcc)}
              className="text-[11px] text-[#6B6C85] hover:text-[#4F46E5] inline-flex items-center gap-1"
              data-testid="email-compose-cc-toggle"
            >
              Cc/Cci {showCcBcc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          {showCcBcc && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-semibold text-[#6B6C85] w-12">Cc</label>
                <input
                  data-testid="email-compose-cc"
                  value={cc} onChange={(e) => setCc(e.target.value)}
                  className="flex-1 h-8 px-2 text-[12.5px] bg-transparent focus:outline-none border-b border-transparent focus:border-[#C7D2FE]"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] font-semibold text-[#6B6C85] w-12">Cci</label>
                <input
                  data-testid="email-compose-bcc"
                  value={bcc} onChange={(e) => setBcc(e.target.value)}
                  className="flex-1 h-8 px-2 text-[12.5px] bg-transparent focus:outline-none border-b border-transparent focus:border-[#C7D2FE]"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold text-[#6B6C85] w-12">Objet</label>
            <input
              data-testid="email-compose-subject"
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet du message"
              className="flex-1 h-8 px-2 text-[12.5px] bg-transparent focus:outline-none border-b border-transparent focus:border-[#C7D2FE] font-medium"
            />
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-3 py-1.5 border-b border-[#EFF0F6] flex items-center gap-1 bg-[#FAFAFE]">
          <button
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={`h-7 w-7 rounded flex items-center justify-center ${editor?.isActive('bold') ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-white'}`}
            title="Gras (Ctrl+B)"
          ><Bold className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={`h-7 w-7 rounded flex items-center justify-center ${editor?.isActive('italic') ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-white'}`}
            title="Italique (Ctrl+I)"
          ><Italic className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={`h-7 w-7 rounded flex items-center justify-center ${editor?.isActive('bulletList') ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-white'}`}
            title="Liste à puces"
          ><List className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            className={`h-7 w-7 rounded flex items-center justify-center ${editor?.isActive('orderedList') ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-white'}`}
            title="Liste numérotée"
          ><ListOrdered className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => {
              const url = window.prompt('URL du lien :', editor?.getAttributes('link')?.href || 'https://');
              if (url === null) return;
              if (url === '') { editor?.chain().focus().unsetLink().run(); return; }
              editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            }}
            className={`h-7 w-7 rounded flex items-center justify-center ${editor?.isActive('link') ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85] hover:bg-white'}`}
            title="Lien"
          ><LinkIcon className="w-3.5 h-3.5" /></button>
          <div className="mx-1 w-px h-4 bg-[#EFF0F6]" />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="h-7 px-2 rounded flex items-center gap-1 text-[11.5px] text-[#6B6C85] hover:bg-white"
            data-testid="email-compose-attach-btn"
          >
            <Paperclip className="w-3.5 h-3.5" /> Joindre
          </button>
          <button
            type="button"
            onClick={() => setTemplatesOpen(true)}
            className="h-7 px-2 rounded flex items-center gap-1 text-[11.5px] text-[#6B6C85] hover:bg-white"
            data-testid="email-compose-templates-btn"
            title="Modèles de mails (F2)"
          >
            <FileText className="w-3.5 h-3.5" /> Modèles
          </button>
          {/* Vague 113 / Wave 1u4 — Signature picker */}
          {signatures.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setSignatureOpen((v) => !v)}
                className="h-7 px-2 rounded flex items-center gap-1 text-[11.5px] text-[#6B6C85] hover:bg-white"
                data-testid="email-compose-signature-btn"
                title="Changer la signature de ce message"
              >
                <PenTool className="w-3.5 h-3.5" /> Signature
                <ChevronDown className="w-3 h-3" />
              </button>
              {signatureOpen && (
                <>
                  <div className="fixed inset-0 z-[55]" onClick={() => setSignatureOpen(false)} />
                  <div
                    className="absolute left-0 top-8 z-[58] w-72 max-h-80 overflow-y-auto bg-white border border-[#EFF0F6] rounded-lg shadow-xl py-1"
                    data-testid="email-compose-signature-list"
                  >
                    {/* Wave 1u5 — Continuous size slider (replaces fixed S/M/L/XL).
                        Drag from 40 % (très petit) to 120 % (large) — the image
                        and text scale proportionally. */}
                    <div className="px-3 py-2 border-b border-[#EFF0F6]">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10.5px] font-semibold text-[#6B6C85] uppercase tracking-wide">Taille</span>
                        <span className="text-[10.5px] text-[#4F46E5] font-bold tabular-nums" data-testid="email-compose-signature-scale-value">
                          {Math.round(signatureScale * 100)}%
                        </span>
                      </div>
                      <input
                        type="range"
                        min="40"
                        max="120"
                        step="5"
                        value={Math.round(signatureScale * 100)}
                        onChange={(e) => applySignatureScale(parseInt(e.target.value, 10) / 100)}
                        data-testid="email-compose-signature-scale-slider"
                        className="w-full h-1 bg-[#E5E7EB] rounded-lg appearance-none cursor-pointer accent-[#4F46E5]"
                      />
                      <div className="flex justify-between text-[9.5px] text-[#8E8FA5] mt-0.5">
                        <span>petit</span>
                        <span>grand</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => applySignature({ id: '', html: '' })}
                      className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-[#FAFAFE] ${!selectedSignatureId ? 'bg-[#EEF2FF] text-[#4F46E5] font-semibold' : 'text-[#4A4B63]'}`}
                      data-testid="email-compose-signature-none"
                    >
                      Sans signature
                    </button>
                    {signatures.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => applySignature(s)}
                        className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-[#FAFAFE] ${selectedSignatureId === s.id ? 'bg-[#EEF2FF] text-[#4F46E5] font-semibold' : 'text-[#4A4B63]'}`}
                        data-testid={`email-compose-signature-${s.id}`}
                        title={s.email || ''}
                      >
                        <div className="truncate">{s.label}</div>
                        {s.email && <div className="text-[10.5px] text-[#8E8FA5] truncate">{s.email}{s.scope === 'shared' && ' · partagée'}</div>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPickFiles} />
          <div className="ml-auto text-[10.5px] text-[#8E8FA5]">
            {savingDraft ? <Loader2 className="inline w-3 h-3 animate-spin mr-1" /> : draftSavedAt ? `Brouillon · ${draftSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" data-testid="email-compose-body">
          <EditorContent editor={editor} />
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="px-3 py-2 border-t border-[#EFF0F6] flex flex-wrap gap-1.5" data-testid="email-compose-attachments">
            {attachments.map((a, idx) => (
              <div key={idx} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-[#EFF0F6] bg-[#F6F7FB] text-[11px] text-[#4A4B63]">
                <Paperclip className="w-3 h-3" />
                <span className="truncate max-w-[180px]">{a.filename}</span>
                <span className="text-[10px] text-[#8E8FA5]">({formatBytes(a.size)})</span>
                <button onClick={() => removeAttachment(idx)} className="text-[#B91C1C] hover:opacity-80" title="Retirer">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <footer className="px-3 py-2 border-t border-[#EFF0F6] flex items-center gap-2 bg-white rounded-b-xl">
          <button
            onClick={send}
            disabled={sending || !to.trim()}
            data-testid="email-compose-send-btn"
            className="h-9 px-4 rounded-lg brand-gradient text-white text-[12.5px] font-semibold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Envoyer
          </button>
          <button
            onClick={() => saveDraft(true)}
            disabled={savingDraft}
            data-testid="email-compose-save-draft-btn"
            className="h-9 px-3 rounded-lg border border-[#EFF0F6] bg-white text-[#4A4B63] text-[12.5px] font-medium inline-flex items-center gap-2 hover:bg-[#FAFAFE] disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> Enregistrer
          </button>
          <div className="flex-1" />
          <button
            onClick={discardDraft}
            data-testid="email-compose-discard-btn"
            className="h-9 w-9 rounded-lg border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center"
            title="Supprimer le brouillon"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </footer>
      </div>
      {/* TipTap minimal styling (inline so component is self-contained) */}
      <style>{`
        .tiptap-mailbox-editor { line-height: 1.5; }
        .tiptap-mailbox-editor p { margin: 0 0 0.5em 0; }
        .tiptap-mailbox-editor a { color: #4F46E5; text-decoration: underline; }
        .tiptap-mailbox-editor ul, .tiptap-mailbox-editor ol { padding-left: 1.5em; margin: 0.5em 0; }
        .tiptap-mailbox-editor ul { list-style: disc; }
        .tiptap-mailbox-editor ol { list-style: decimal; }
        .tiptap-mailbox-editor:focus-visible { outline: none; }
      `}</style>
      {/* Wave 1u3 Phase C / F2 — Mail templates popover */}
      <EmailTemplatesPopover
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onInsert={({ subject: tplSubject, body_html: tplBody }) => {
          // Wave 1u4 — interpolate CRM-aware variables when inserting.
          const resolvedSubject = interpolate(tplSubject);
          const resolvedBody = interpolate(tplBody || '');
          if (resolvedSubject) setSubject(resolvedSubject);
          if (editor && !editor.isDestroyed) {
            // Insert template body BEFORE the signature/quoted block so that
            // the user's existing context (signature, reply quote) is preserved.
            const currentBody = editor.getHTML() || '';
            editor.commands.setContent(resolvedBody + currentBody);
          }
          const matched = Object.keys(templateVars || {}).length > 0 && templateVars.company_name;
          toast({
            title: 'Modèle inséré',
            description: matched ? `Variables remplacées pour ${templateVars.company_name}` : 'Variables non remplacées (aucun client rattaché)',
          });
        }}
        currentSubject={subject}
        currentBodyHtml={editor?.getHTML() || ''}
      />
    </div>
  );
}
